/// FOURJS_START_COPYRIGHT(D,2025)
/// Property of Four Js*
/// (c) Copyright Four Js 2025, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT
"use strict";

modulum(
  "GbcItemWidgetFactory",
  ["I18NService", "DebugService", "ItemWidget"],
  /**
   * @param {gbc} context
   * @param {classes} cls
   */
  function (context, cls) {
    /**
     * This factory build preconfigured Item widgets
     * to be placed in chromebar or sidebar
     * @param {*} context
     * @param {*} cls
     */
    cls.GbcItemWidgetFactory = /** @lends classes.GbcItemWidgetFactory */ {
      /**
       * List of GBC menu items which are authorized.
       * This is a max items list definition, the displayed list comes from the theme variable 'gbc-ChromeBar-visible-items'
       * @type {string[]}
       */
      gbcItemDefinitionList: ["filter", "uploadStatus", "runInGdc", "debug", "settings", "close"],
      /**
       * Set the text, title and image to the widget
       * @param {classes.ItemWidget} itemWidget The item widget
       * @param {string} text The I18N key for the text that will be display on the item
       * @param {string} title The I18N key for the title that will be displayed in the tooltip
       * @param {string} image The image as a zmdi icon
       * @param {string} auiName The name to set as auiName HTML attribute
       */
      _configureItem(itemWidget, text, title, image, auiName) {
        itemWidget.setText(gbc.I18NService.t(text));
        itemWidget.setTitle(gbc.I18NService.t(title));
        itemWidget.setImage(image);
        itemWidget.setQAInfo("name", `${auiName}`);
      },

      //#region CHROMEBAR

      /**
       * Create a close button
       * - modifier css class : chromeItem
       * - auiName : close
       * - icon : close
       * - Cant overflow on IOS
       * @param {any} opts
       * @returns {classes.ItemWidget}
       */
      close(opts) {
        const closeItem = new cls.ItemWidget(opts);
        const text = "gwc.main.chromebar.close";
        this._configureItem(closeItem, text, text, "zmdi-close-circle", "close");

        // Set an aui name as it's a genero action
        closeItem.setAuiName("close");
        closeItem.cantOverflow = gbc.ThemeService.getValue("gbc-ChromeBar-pin-close-button");
        closeItem.onClick(() => context.HostService.closeCurrentWindow());
        return closeItem;
      },

      /**
       * Create a setting button
       * - modifier css class : chromeItem
       * - auiName : gbc_settings
       * - icon : cog
       * - onClick : HostService.showSettingsPanel
       * Bind button notification badge to storedSettingService
       * @param {any} opts
       * @returns {classes.ItemWidget}
       */
      settings(opts) {
        /** @type {classes.ItemWidget} */
        const settingsItem = cls.WidgetFactory.createWidget("Item", opts);
        const text = "gwc.main.chromebar.settings";
        this._configureItem(settingsItem, text, text, "zmdi-cog", "gbc_settings");
        // Add info about browser storage full
        if (gbc.StoredSettingsService.quotaExceededError) {
          settingsItem.addClasses("error");
        }
        gbc.StoredSettingsService.onQuotaExceededError(settingsItem.addClasses.bind(settingsItem, "error"));
        const settingsDestroyHandler = () => {
          if (settingsItem && settingsItem.environmentAlertHandler) {
            settingsItem.environmentAlertHandler();
            settingsItem.environmentAlertHandler = null;
          }
        };
        const notifyEnvironment = function (evt, invoker, missingAPIs) {
          settingsItem.toggleClass("hasNotification", missingAPIs.length > 0);
          settingsDestroyHandler();
        };

        const currentSession = context.SessionService.getCurrent();
        settingsItem.environmentAlertHandler = currentSession?.when("environmentAlert", notifyEnvironment.bind(this));
        settingsItem.onDestroy(settingsDestroyHandler);
        settingsItem.onClick(() => context.HostService.showSettingsPanel(settingsItem.getBuildParameters()));

        return settingsItem;
      },

      /**
       * Create an overflow menu button
       * - modifier css class : chromeItem
       * - auiName : gbc_chromeBarOverflowMenuButton
       * - icon : 3 vertical dots
       * - onClick : Open right floating panel
       * - Can't overflow
       * @param {any} opts
       * @returns {classes.ItemWidget}
       */
      openOverflowMenu(opts) {
        const overflowMenuItem = cls.WidgetFactory.createWidget("Item", opts);
        this._configureItem(overflowMenuItem, "", "Open overflow menu", "zmdi-dots-vertical", "gbc_chromeBarOverflowMenuButton");
        overflowMenuItem.onClick(() => context.ChromeActionService.openRightFloatingPanel());
        overflowMenuItem.cantOverflow = true;
        return overflowMenuItem;
      },

      /**
       * Create a debug button
       * - modifier css class : chromeItem
       * - auiName : gbc_debug
       * - icon : memory chip
       * Register this item on the debug service
       * @param {any} opts
       * @returns {classes.ItemWidget}
       */
      debug(opts) {
        const debugItem = cls.WidgetFactory.createWidget("Item", opts);
        this._configureItem(debugItem, "gwc.main.chromebar.debugTools", "gwc.main.chromebar.debugTools", "zmdi-memory", "gbc_debug");
        context.DebugService.registerDebugUi(debugItem);
        debugItem.onClick(() => context.DebugService.show());
        debugItem.onDestroy(() => context.DebugService.unregisterDebugUi(debugItem));
        return debugItem;
      },

      // TODO GBC-5056 : Not sure this factory is usefull, as the filter widget should not inherit from ChromeBarItem
      /**
       * Create a filter widget
       * - auiName : gbc_filter
       * - icon : filter
       * - Cant Overflow
       * @param {any} opts
       * @returns {classes.FilterWidget}
       */
      filter(opts) {
        const filterItem = cls.WidgetFactory.createWidget("Item", opts);
        const text = "gwc.main.chromebar.filter";
        this._configureItem(filterItem, text, text, "zmdi-filter-variant", "gbc_filter");

        filterItem.onClick(() => context.FilterService.invokeFilter());
        // Force the filter item to be at start
        filterItem.setStyle({ order: -1 });
        filterItem.cantOverflow = true;

        return filterItem;
      },

      /**
       * Create an upload status item
       * - modifier css class : chromeItem
       * - auiName : gbc_upload
       * - icon : upload
       * - Cant overflow
       * @param {any} opts
       * @returns {classes.ChromeBarItemUploadStatusWidget}
       */
      uploadStatus(opts) {
        const uploadStatusItem = cls.WidgetFactory.createWidget("ChromeBarItemUploadStatus", opts);
        const text = gbc.I18NService.t("gwc.file.upload.processing");
        this._configureItem(uploadStatusItem, text, text, "zmdi-upload", "gbc_upload");
        uploadStatusItem.setHidden(true);

        uploadStatusItem.cantOverflow = true;

        return uploadStatusItem;
      },

      //#endregion

      //#region SIDEBAR

      /**
       * Create a dock button for SideBar Drawer
       * - auiName : gbc_dockRailButton
       * - icon : dock left
       * - onClick : dock sidebar drawer
       * @param {any} opts
       * @returns {classes.ItemWidget}
       */
      dockDrawer(opts) {
        /** @type {classes.ItemWidget} */
        const dock = cls.WidgetFactory.createWidget("Item", opts);
        this._configureItem(dock, "", "dock", "zmdi-dock-left", "gbc_dockRailButton");
        const dockDrawer = () => {
          if (!context.MainContainerService.isSideBarDrawerVisible()) {
            context.MainContainerService.showSideBarDrawer();
          }
          context.MainContainerService.dockSideBarDrawer();
        };
        dock.addClasses("gbc_ItemWidget--dockButton");
        dock.onClick(dockDrawer);
        return dock;
      },

      /**
       * Create an undock button for SideBar Drawer
       * - auiName : gbc_unDockRailButton
       * - icon : dock right
       * - onClick : Undock sidebar drawer
       * @param {any} opts
       * @returns {classes.ItemWidget}
       */
      undockDrawer(opts) {
        /** @type {classes.ItemWidget} */
        const undockItem = cls.WidgetFactory.createWidget("Item", opts);
        this._configureItem(undockItem, "", "Undock", "zmdi-dock-right", "gbc_unDockRailButton");
        undockItem.addClasses("gbc_ItemWidget--undockButton");
        undockItem.onClick(() => context.MainContainerService.unDockSideBarDrawer());
        return undockItem;
      },

      /**
       * Create a dock button for FloatingPanel
       * - auiName : gbc_dockFloatingPanelButton
       * - icon : dock left
       * - onClick : dock FloatingPanel
       * @param {any} opts
       * @returns {classes.ItemWidget}
       */
      dockFloatingPanel(opts) {
        /** @type {classes.ItemWidget} */
        const dock = cls.WidgetFactory.createWidget("Item", opts);
        this._configureItem(dock, "", "dock", "zmdi-dock-left", "gbc_dockFloatingPanelButton");
        const dockFloatingPanel = () => {
          context.MainContainerService.dockFloatingPanel();
        };
        dock.addClasses("gbc_ItemWidget--dockButton");
        dock.onClick(dockFloatingPanel);
        return dock;
      },

      /**
       * Create an undock button for FloatingPanel
       * - auiName : gbc_unDockFloatingPanelButton
       * - icon : dock right
       * - onClick : Undock FloatingPanel
       * @param {any} opts
       * @returns {classes.ItemWidget}
       */
      undockFloatingPanel(opts) {
        /** @type {classes.ItemWidget} */
        const undockItem = cls.WidgetFactory.createWidget("Item", opts);
        this._configureItem(undockItem, "", "Undock", "zmdi-dock-right", "gbc_unDockFloatingPanelButton");
        const undockFloatingPanel = () => {
          context.MainContainerService.unDockFloatingPanel();
        };
        undockItem.addClasses("gbc_ItemWidget--undockButton");
        undockItem.onClick(undockFloatingPanel);
        return undockItem;
      },

      //#endregion

      //#region DEBUG PANEL

      /**
       * Create a runInGdc button
       * - auiName : gbc_runInGdc
       * - icon : play
       * Register this item on the debug service
       * @param {any} opts
       * @returns {classes.ItemWidget}
       */
      runInGdc(opts) {
        const runInGdcItem = cls.WidgetFactory.createWidget("Item", opts);

        const text = "gwc.main.chromebar.runInGDC";
        this._configureItem(runInGdcItem, text, text, "zmdi-play", "gbc_runInGdc");
        gbc.DebugService.registerDebugUi(runInGdcItem);

        const action = function () {
          const url = new URL(window.location.href);
          const name = url.pathname.substring(url.pathname.lastIndexOf("/") + 1);
          const shortcut = `<?xml version="1.0" encoding="utf-8"?>
<fjs configVersion="2" product="Genero Desktop Client">
  <Shortcuts>
    <Shortcut name="${name}" authenticationMode="standard" type="http" proxyType="monitor" url="${window.location.href}"/>
  </Shortcuts>
</fjs>`;

          const shortcutFile = new Blob([shortcut], {
            type: "application/genero-gdc"
          });

          const fileName = name + ".gdc";
          const a = document.createElement("a");
          a.style.display = "none";
          // firefox needs to have element in DOM
          document.body.appendChild(a);
          a.href = window.URL.createObjectURL(shortcutFile);
          a.download = fileName;
          a.click();
          a.remove();
        };

        runInGdcItem.onClick(action);

        runInGdcItem.onDestroy(() => context.DebugService.unregisterDebugUi(runInGdcItem));
        return runInGdcItem;
      }

      //#endregion
    };
  }
);
